import { type HTMLAttributes } from "react";

import { CaretRightIcon } from "#icons";
import { customTv } from "#utils";
import { Link as AriaLink, type LinkProps as AriaLinkProps } from "react-aria-components";

import { EntityCardHeader, type EntityCardHeaderProps } from "./EntityCardHeader";
import { cardBase } from "./styles";

const entityCardStyles = customTv({
  base: cardBase,
  variants: {
    layout: {
      inline: "items-center gap-xs p-sm",
      stacked: "flex-col gap-xs p-md",
    },
    interactive: {
      true: "cursor-pointer outline-none transition-colors duration-200 hover:border-outline-brand focus-visible:border-outline-brand data-[disabled]:cursor-not-allowed",
    },
  },
});

/** Presentational props shared by both EntityCard variants (the `trailing` slot is managed internally). */
type SharedEntityCardProps = Omit<EntityCardHeaderProps, "trailing"> & {
  /** Additional class names merged onto the card */
  className?: string;
};

/** Link variant of EntityCard; forwards interactive props (onPress, aria-*, data-*, isDisabled, …) to react-aria's Link. */
export type LinkEntityCardProps = SharedEntityCardProps &
  Omit<AriaLinkProps, "className" | "children" | "title"> & {
    href: string;
  };

/** Static variant of EntityCard; forwards standard div attributes. */
export type StaticEntityCardProps = SharedEntityCardProps &
  Omit<HTMLAttributes<HTMLDivElement>, "className" | "children" | "title"> & {
    href?: undefined;
  };

/** Props for the EntityCard component */
export type EntityCardProps = LinkEntityCardProps | StaticEntityCardProps;

/**
 * Card surfacing a related entity (account, policy, …) with an optional link
 * affordance. Supports two arrangements via `layout`: `"inline"` (label and
 * title share a row) and `"stacked"` (title sits beneath the label). Renders as
 * a react-aria Link when `href` is provided, otherwise as a plain div. The
 * shape of `props` discriminates which extra attributes are accepted
 * (AriaLinkProps vs. HTMLDivAttributes).
 *
 * For an expandable, disclosure-style card use `ExpandableEntityCard`.
 */
export function EntityCard(props: EntityCardProps) {
  const { icon, label, title, meta, aside, layout = "inline", className } = props;
  const isLink = props.href !== undefined;

  const header = (
    <EntityCardHeader
      aside={aside}
      icon={icon}
      label={label}
      layout={layout}
      meta={meta}
      title={title}
      trailing={
        isLink ? (
          <span className="flex shrink-0 items-center justify-center text-text-primary">
            <CaretRightIcon size={16} />
          </span>
        ) : null
      }
    />
  );

  if (props.href !== undefined) {
    const {
      icon: _icon,
      label: _label,
      title: _title,
      meta: _meta,
      aside: _aside,
      layout: _layout,
      className: _className,
      ...linkRest
    } = props;
    return (
      <AriaLink
        {...linkRest}
        className={entityCardStyles({ layout, interactive: true, className })}
      >
        {header}
      </AriaLink>
    );
  }

  const {
    icon: _icon,
    label: _label,
    title: _title,
    meta: _meta,
    aside: _aside,
    layout: _layout,
    className: _className,
    href: _href,
    ...divRest
  } = props;
  return (
    <div {...divRest} className={entityCardStyles({ layout, className })}>
      {header}
    </div>
  );
}
